import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { verifyEmail } from '../constants/api';
import { COLORS } from '../constants/theme';

export default function VerifyPage() {
  const isMobile = window.innerWidth < 768;
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email ?? '';
  const devOtp = location.state?.devOtp ?? '';

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(600);
  const inputs = useRef([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer(t => t <= 1 ? 0 : t - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatTimer = () => {
    const m = Math.floor(timer / 60);
    const s = timer % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleChange = (val, index) => {
    const cleaned = val.replace(/[^0-9]/g, '');
    const newOtp = [...otp];
    newOtp[index] = cleaned;
    setOtp(newOtp);
    if (cleaned && index < 5) inputs.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) return setError('Please enter the full 6-digit code.');
    setLoading(true);
    setError('');
    try {
      await verifyEmail({ email, otp: code });
      navigate('/login', { state: { verified: true } });
    } catch (err) {
      const msg = err?.response?.data ?? 'Invalid or expired code.';
      setError(typeof msg === 'string' ? msg : 'Invalid or expired code.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: isMobile ? 42 : 52,
    height: isMobile ? 48 : 60,
    borderRadius: 10,
    border: `2px solid ${COLORS.border}`,
    fontSize: isMobile ? 20 : 24,
    fontWeight: 800,
    textAlign: 'center',
    color: COLORS.ink,
    background: COLORS.bg,
    outline: 'none',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ minHeight: '100vh', background: COLORS.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '24px 16px' : '24px' }}>
      <div style={{ width: '100%', maxWidth: 480, background: 'white', borderRadius: 20, padding: isMobile ? '32px 24px' : '48px', border: `1px solid ${COLORS.border}`, boxShadow: '0 4px 40px rgba(0,0,0,0.06)' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 44, marginBottom: 14 }}>📧</div>
          <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 900, color: COLORS.ink, marginBottom: 8 }}>Check your email</div>
          <div style={{ fontSize: 14, color: COLORS.inkLight, lineHeight: 1.6 }}>
            We sent a 6-digit verification code to
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.accent, marginTop: 4 }}>
            {email}
          </div>
        </div>

        {/* Dev OTP hint */}
        {devOtp && (
          <div style={{ background: COLORS.accentLight, border: `1px solid #B6DFC7`, borderRadius: 10, padding: '12px 16px', marginBottom: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent, marginBottom: 4 }}>Dev Mode — Your OTP</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: COLORS.accent, letterSpacing: 4 }}>{devOtp}</div>
          </div>
        )}

        <form onSubmit={handleVerify}>
          {/* OTP inputs */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: isMobile ? 6 : 10, marginBottom: 24 }}>
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={ref => inputs.current[index] = ref}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleChange(e.target.value, index)}
                onKeyDown={e => handleKeyDown(e, index)}
                autoFocus={index === 0}
                style={{
                  ...inputStyle,
                  borderColor: digit ? COLORS.accent : COLORS.border,
                  background: digit ? COLORS.accentLight : COLORS.bg,
                }}
                onFocus={e => e.target.style.borderColor = COLORS.accent}
                onBlur={e => e.target.style.borderColor = digit ? COLORS.accent : COLORS.border}
              />
            ))}
          </div>

          {/* Timer */}
          <div style={{ textAlign: 'center', marginBottom: 24, fontSize: 13, color: COLORS.inkLight }}>
            {timer > 0 ? (
              <>Code expires in <span style={{ color: COLORS.accent, fontWeight: 700 }}>{formatTimer()}</span></>
            ) : (
              <span style={{ color: COLORS.danger, fontWeight: 600 }}>Code expired. Please register again.</span>
            )}
          </div>

          {error && (
            <div style={{ background: '#FEE2E2', border: '1px solid #fca5a5', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: COLORS.danger, fontWeight: 600, marginBottom: 16, textAlign: 'center' }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={otp.join('').length !== 6 || loading || timer === 0}
            style={{ width: '100%', background: COLORS.accent, color: 'white', border: 'none', borderRadius: 10, padding: '14px', fontSize: 15, fontWeight: 800, cursor: 'pointer', opacity: (otp.join('').length !== 6 || loading || timer === 0) ? 0.6 : 1, fontFamily: 'inherit' }}
          >
            {loading ? 'Verifying...' : 'Verify Email'}
          </button>
        </form>

        <div style={{ marginTop: 20, textAlign: 'center', fontSize: 14, color: COLORS.inkLight }}>
          Wrong email?{' '}
          <span onClick={() => navigate('/register')} style={{ color: COLORS.accent, fontWeight: 700, cursor: 'pointer' }}>Go back</span>
        </div>
      </div>
    </div>
  );
}